import { useCallback, useMemo } from "react";
import { parseAsInteger, parseAsString, useQueryStates } from "nuqs";

export const FILTER_KEYS = ["decisions", "syzitisi", "court", "diadikasia", "epan"];

// Classic handlers may write "null"/"all" into the URL; treat them as unset.
const normalize = (value) =>
  value === null || value === undefined || value === "" || value === "null" || value === "all"
    ? ""
    : value;

// List filters stored in the URL, plus the desktop search `q` and the
// 0-based `page`. The default keys are the Αστικά ones (same keys as the
// classic page, so links like ?court=… or ?epan=pending work in both
// layouts); other case types pass their own. `keys` must be a module-level
// constant.
export default function useCaseFilters(keys = FILTER_KEYS) {
  const parsers = useMemo(
    () => ({
      ...Object.fromEntries(keys.map((k) => [k, parseAsString])),
      q: parseAsString,
      page: parseAsInteger.withDefault(0),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keys.join("|")]
  );
  const [state, setState] = useQueryStates(parsers, { history: "replace" });

  const values = keys.map((k) => normalize(state[k]));
  const filters = useMemo(
    () => Object.fromEntries(keys.map((k, i) => [k, values[i]])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    values
  );

  const activeCount = keys.filter((k) => filters[k]).length;

  // Any filter/search change goes back to the first page.
  const setFilter = useCallback(
    (key, value) => setState({ [key]: value ? value : null, page: null }),
    [setState]
  );

  const setSearch = useCallback(
    (value) => setState({ q: value ? value : null, page: null }),
    [setState]
  );

  const clearFilters = useCallback(
    () => setState({ ...Object.fromEntries(keys.map((k) => [k, null])), page: null }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setState, keys.join("|")]
  );

  const setPage = useCallback(
    (value) =>
      setState((prev) => {
        const next = typeof value === "function" ? value(prev.page) : value;
        return { page: next > 0 ? next : null };
      }),
    [setState]
  );

  return {
    filters,
    activeCount,
    search: state.q ?? "",
    page: state.page,
    setFilter,
    setSearch,
    clearFilters,
    setPage,
  };
}
